import { useState } from 'react'
import { PALETTE as P } from '../tokens'
import { useAuth } from '../context/AuthContext'
import { usersApi } from '../api/users'

/**
 * AI data-sharing consent (App Store Guidelines 5.1.1(i) / 5.1.2(i)).
 *
 * Distinct from research consent. This is the affirmative opt-in that
 * governs whether ANY user data is transmitted to Anthropic. The
 * backend fails closed: NULL or false means zero Anthropic calls for
 * this account, so the modal's answer is enforced server-side, not
 * just cosmetically.
 *
 * Surfaces:
 *  • AIConsentModal — ProtectedRoute shows it for existing users whose
 *    ai_data_consent is NULL (they consented under an older,
 *    research-framed ask that never named the AI provider — they must
 *    re-consent).
 *  • Onboarding screen 2 reuses AIDisclosureBody + AIDeclineExplainer
 *    for new users, before the brain-dump is written.
 */

// The literal disclosure — kept in one place so onboarding, the modal,
// and Settings describe the exact same data flows. Accuracy is
// load-bearing: tests/test_ai_payload_privacy.py enforces the "never"
// claims against real outgoing payloads.
export const AI_DATA_POINTS = [
  'The reflections you write — check-ins, journal entries, and chat messages',
  'Your onboarding and Foundations answers',
  'Your wellness scores and trends',
  'Simple labels from your wearable (like “sleep: low”) — never raw readings',
]

export const AI_NEVER_LINE =
  'Never your name, email, birth date, or account details.'

export const AI_RECIPIENT_LINE =
  'This information is sent to Anthropic — the AI company that makes ' +
  'Claude — to generate your insights, Today’s read, Deep Dive ' +
  'analyses, Haiven AI chat, and report narratives.'

export const AI_CONTROL_LINE =
  'You can change this anytime in Settings. Turning it off stops all ' +
  'AI data sharing immediately.'

export function AIDisclosureBody({ compact = false }) {
  return (
    <div style={{ textAlign: 'left' }}>
      <p style={{
        fontSize: compact ? 12.5 : 13.5, color: P.textMid, fontWeight: 300,
        lineHeight: 1.65, marginBottom: 10,
      }}>
        {AI_RECIPIENT_LINE}
      </p>
      <p style={{
        fontSize: 11, color: P.primary, letterSpacing: '0.1em',
        fontWeight: 500, margin: '0 0 6px',
      }}>
        WHAT&rsquo;S SHARED
      </p>
      <ul style={{
        listStyle: 'none', padding: 0, margin: '0 0 12px',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        {AI_DATA_POINTS.map((line) => (
          <li key={line} style={{
            fontSize: compact ? 12 : 12.5, color: P.textMid,
            fontWeight: 300, lineHeight: 1.55,
            paddingLeft: 14, position: 'relative',
          }}>
            <span style={{
              position: 'absolute', left: 0, top: 7,
              width: 5, height: 5, borderRadius: '50%',
              background: P.primaryMid,
            }} />
            {line}
          </li>
        ))}
      </ul>
      <p style={{
        fontSize: compact ? 12 : 12.5, color: P.text, fontWeight: 400,
        lineHeight: 1.55, marginBottom: 10,
      }}>
        {AI_NEVER_LINE}
      </p>
      <p style={{
        fontSize: 11.5, color: P.textMute, fontWeight: 300,
        lineHeight: 1.55, margin: 0,
      }}>
        {AI_CONTROL_LINE}
      </p>
    </div>
  )
}

// What truthfully changes on decline. Deterministic features are named
// as staying — withholding them would be coercive and untrue.
export const AI_DECLINE_POINTS = [
  {
    label: 'Works fully',
    text: 'Your Signal score, Signal history, and Body domain — computed on Haiven’s own servers, no AI involved.',
  },
  {
    label: 'Works, a bit less precise',
    text: 'Mind & Behavior scores come from your self-reports alone, without the journal-derived layer.',
  },
  {
    label: 'Plainer wording',
    text: 'Today’s read, insights, and reports use standard wording instead of AI writing.',
  },
  {
    label: 'Not available',
    text: 'Deep Dive analysis and Haiven AI chat.',
  },
  // Crisis detection reads the person's writing with the AI model, so it
  // is off when AI is declined. That is the policy (checking means sending
  // the text, which is what they declined); this line is the disclosure.
  {
    label: 'Not available',
    text: 'Haiven won’t read your writing for signs you may need support. If you’re ever in crisis, call or text 988.',
  },
]

export function AIDeclineExplainer({ onConfirm, onBack, submitting = false }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2100,
      background: 'rgba(28,24,20,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-decline-title"
        style={{
          background: P.bg, borderRadius: 22, padding: '28px 26px 24px',
          maxWidth: 420, width: '100%',
          maxHeight: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px)',
          overflowY: 'auto',
          boxShadow: '0 12px 44px rgba(28,24,20,0.22)',
        }}
      >
        <h3
          id="ai-decline-title"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 22, fontStyle: 'italic', fontWeight: 400,
            color: P.text, lineHeight: 1.25, marginBottom: 14,
          }}
        >
          Here&rsquo;s what changes without AI
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          {AI_DECLINE_POINTS.map(({ label, text }) => (
            <div key={text}>
              <p style={{
                fontSize: 10.5, color: P.primary, letterSpacing: '0.1em',
                fontWeight: 500, margin: '0 0 3px', textTransform: 'uppercase',
              }}>
                {label}
              </p>
              <p style={{
                fontSize: 12.5, color: P.textMid, fontWeight: 300,
                lineHeight: 1.55, margin: 0,
              }}>
                {text}
              </p>
            </div>
          ))}
        </div>
        <button
          onClick={onConfirm}
          disabled={submitting}
          style={{
            width: '100%', padding: 14,
            background: P.dark, border: 'none', borderRadius: 14,
            cursor: submitting ? 'default' : 'pointer',
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 15, fontStyle: 'italic', color: P.bg,
            opacity: submitting ? 0.5 : 1, marginBottom: 10,
          }}
        >
          Continue without AI
        </button>
        <button
          onClick={onBack}
          disabled={submitting}
          style={{
            width: '100%', padding: 13,
            background: 'none', border: `1.5px solid ${P.border}`,
            borderRadius: 14, cursor: submitting ? 'default' : 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13, fontWeight: 400, color: P.textMid,
            opacity: submitting ? 0.5 : 1,
          }}
        >
          Go back
        </button>
      </div>
    </div>
  )
}

export function AIConsentModal() {
  const { token, patchUser } = useAuth()
  const [submitting, setSubmitting] = useState(false)
  const [showDecline, setShowDecline] = useState(false)

  async function respond(aiConsent) {
    if (submitting) return
    setSubmitting(true)

    // Optimistic patch so the modal unmounts immediately. Unlike the
    // research modal, a failed PATCH here is privacy-safe: the backend
    // fails closed (NULL = no AI calls), so the worst case of a lost
    // opt-in is the modal reappearing — never data sent without consent.
    patchUser({ ai_data_consent: aiConsent })

    try {
      await usersApi.setAIConsent(aiConsent, token)
    } catch {
      // Best-effort; a later /auth/me refresh reconciles.
    } finally {
      setSubmitting(false)
    }
  }

  if (showDecline) {
    return (
      <AIDeclineExplainer
        submitting={submitting}
        onConfirm={() => respond(false)}
        onBack={() => setShowDecline(false)}
      />
    )
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(28,24,20,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20, fontFamily: "'DM Sans', sans-serif",
      overflowY: 'auto',
    }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-consent-title"
        style={{
          background: P.bg, borderRadius: 22, padding: '30px 26px 24px',
          maxWidth: 420, width: '100%', margin: 'auto',
          maxHeight: 'calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 24px)',
          overflowY: 'auto',
          boxShadow: '0 12px 44px rgba(28,24,20,0.22)',
        }}
      >
        <div style={{
          fontSize: 10, color: P.primary,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          fontWeight: 500, marginBottom: 12, textAlign: 'center',
        }}>
          AI Data Sharing
        </div>

        <h2
          id="ai-consent-title"
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 24, fontStyle: 'italic', fontWeight: 400,
            color: P.text, lineHeight: 1.2, marginBottom: 14,
            textAlign: 'center',
          }}
        >
          Haiven&rsquo;s insights are AI&#8209;powered
        </h2>

        <div style={{ marginBottom: 20 }}>
          <AIDisclosureBody />
        </div>

        <button
          onClick={() => respond(true)}
          disabled={submitting}
          style={{
            width: '100%', padding: 15,
            background: P.dark, border: 'none', borderRadius: 14,
            cursor: submitting ? 'default' : 'pointer',
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 16, fontStyle: 'italic', color: P.bg,
            letterSpacing: '0.01em',
            opacity: submitting ? 0.5 : 1,
            transition: 'opacity 0.15s', marginBottom: 10,
          }}
        >
          Turn on AI analysis
        </button>

        <button
          onClick={() => setShowDecline(true)}
          disabled={submitting}
          style={{
            width: '100%', padding: 14,
            background: 'none', border: `1.5px solid ${P.border}`,
            borderRadius: 14, cursor: submitting ? 'default' : 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13.5, fontWeight: 400, color: P.textMid,
            opacity: submitting ? 0.5 : 1, transition: 'all 0.15s',
          }}
        >
          Continue without AI
        </button>
      </div>
    </div>
  )
}
